import { League } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { LeagueCard, PatternBackground } from '@/src/presentation/components';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { FlatList, Modal, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { createStyles } from './LeaguesScreen.styles';

export interface LeaguesScreenProps {
  leagues: League[];
  leagueStats: Map<string, { tournamentCount: number; participantCount: number }>;
  loading: boolean;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  menuVisible: boolean;
  setMenuVisible: (value: boolean) => void;
  handleLeaguePress: (leagueId: string) => void;
  handleCreateLeague: () => void;
  handleMenuPress: (league: League, event: any) => void;
  handleEdit: () => void;
  handleDelete: () => void;
  onRefresh: () => void;
  colors: any;
}

export const LeaguesScreen = ({
  leagues,
  leagueStats,
  loading,
  searchQuery,
  setSearchQuery,
  menuVisible,
  setMenuVisible,
  handleLeaguePress,
  handleCreateLeague,
  handleMenuPress,
  handleEdit,
  handleDelete,
  onRefresh,
  colors,
}: LeaguesScreenProps) => {
  const { t } = useTranslation();
  const styles = createStyles(colors);

  const filteredLeagues = leagues.filter(
    (l) => l.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const renderEmpty = () => (
    <View style={styles.emptyContainer}>
      <MaterialCommunityIcons name="trophy-outline" size={64} color={colors.textTertiary} />
      <Text style={styles.emptyText}>{t('leagues.noLeaguesYet')}</Text>
      <Text style={styles.emptySubtext}>{t('leagues.createFirstLeague')}</Text>
    </View>
  );

  return (
    <PatternBackground>
      <View style={styles.header}>
        <View style={styles.searchContainer}>
          <Ionicons name="search" size={20} color={colors.textTertiary} style={styles.searchIcon} />
          <TextInput
            style={styles.searchInput}
            placeholder={t('common.search')}
            placeholderTextColor={colors.textTertiary}
            value={searchQuery}
            onChangeText={setSearchQuery}
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity onPress={() => setSearchQuery('')} style={styles.clearButton}>
              <Ionicons name="close-circle" size={20} color={colors.textTertiary} />
            </TouchableOpacity>
          )}
        </View>
        <TouchableOpacity style={styles.createButton} onPress={handleCreateLeague}>
          <Ionicons name="add-circle-outline" size={22} color={colors.primaryText} />
          <Text style={styles.createButtonText}>{t('leagues.createLeague')}</Text>
        </TouchableOpacity>
      </View>

      <FlatList
        data={filteredLeagues}
        keyExtractor={(item) => item.id}
        renderItem={({ item }) => {
          const stats = leagueStats.get(item.id);
          return (
            <LeagueCard
              league={item}
              tournamentCount={stats?.tournamentCount}
              participantCount={stats?.participantCount}
              onPress={() => handleLeaguePress(item.id)}
              onMenuPress={(event) => handleMenuPress(item, event)}
              colors={colors}
            />
          );
        }}
        contentContainerStyle={filteredLeagues.length === 0 ? { flex: 1 } : styles.listContent}
        ListEmptyComponent={!loading ? renderEmpty : null}
        refreshing={loading}
        onRefresh={onRefresh}
      />

      <Modal visible={menuVisible} transparent animationType="fade" onRequestClose={() => setMenuVisible(false)}>
        <TouchableOpacity style={styles.menuOverlay} activeOpacity={1} onPress={() => setMenuVisible(false)}>
          <View style={styles.menuContainer}>
            <TouchableOpacity
              style={styles.menuItem}
              onPress={() => {
                setMenuVisible(false);
                handleEdit();
              }}
            >
              <Ionicons name="create-outline" size={22} color={colors.text} />
              <Text style={[styles.menuText, { color: colors.text }]}>{t('common.edit')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.menuItem}
              onPress={() => {
                setMenuVisible(false);
                handleDelete();
              }}
            >
              <Ionicons name="trash-outline" size={22} color={colors.error} />
              <Text style={[styles.menuText, { color: colors.error }]}>{t('common.delete')}</Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Modal>
    </PatternBackground>
  );
};
